/**
 * I-2195: a refusal that names a role, as a code plus the role's facts.
 *
 * The server words these in English for the CLI and carries the same refusal as `code` and `data`
 * (the role's names and what it was asked to do), so a screen words it from its catalogue in the
 * reader's language (`failures.roleRefusal.*`) instead of showing "Work Area Viewer" or a package's
 * "Producer" inside an English sentence (F-0324: the reason is data, the sentence is the client's).
 */
export const ROLE_REFUSAL_KEYS = {
  role_scope_mismatch: 'scopeMismatch',
  role_never_granted: 'neverGranted',
  role_no_role_assign: 'noRoleAssign',
  role_above_own_role: 'aboveOwnRole',
  role_permissions_not_held: 'permissionsNotHeld',
  role_for_agents: 'agentRole',
  role_guest_permission: 'guestPermission',
  owner_role_cannot_name_owners: 'ownerRoleCannot',
} as const;

export type RoleRefusalCodeType = keyof typeof ROLE_REFUSAL_KEYS;
export type RoleRefusalKeyType = (typeof ROLE_REFUSAL_KEYS)[RoleRefusalCodeType];

/** A role's name as stored (English) and in German when a package installed there words it. */
export interface IRoleRefusalNames {
  readonly en: string;
  readonly de: string | null;
}

export interface IRoleRefusalFacts {
  /** The role's key, so a seeded role under its seeded name reads from the seeded words. */
  readonly roleKey: string | null;
  readonly roleNames: IRoleRefusalNames;
  /** What was asked: granting a role or taking it away. Absent where the refusal is not about either. */
  readonly verb?: 'grant' | 'remove';
  /** The permission a guest role was asked to carry. */
  readonly permission?: string;
  /** Set where the refusal came while a package was being installed, which adds who to ask. */
  readonly during?: 'install';
}

/** The body a role refusal answers with: the English sentence, the code, and the facts. */
export function roleRefusalBody(
  statusCode: number,
  code: RoleRefusalCodeType,
  message: string,
  data: IRoleRefusalFacts,
): { statusCode: number; code: RoleRefusalCodeType; message: string; data: IRoleRefusalFacts } {
  return { statusCode, code, message, data };
}

function isNames(value: unknown): value is IRoleRefusalNames {
  const names = value as { en?: unknown; de?: unknown } | null;
  return typeof names?.en === 'string' && (names.de === null || typeof names.de === 'string');
}

/** The refusal a response body carries, or null when it is not one of these (or an older server's). */
export function roleRefusalOf(
  body: unknown,
): { key: RoleRefusalKeyType; facts: IRoleRefusalFacts } | null {
  const candidate = body as { code?: unknown; data?: Partial<IRoleRefusalFacts> | null } | null;
  const code = candidate?.code;
  if (typeof code !== 'string' || !(code in ROLE_REFUSAL_KEYS)) return null;
  const data = candidate?.data;
  if (!data || !isNames(data.roleNames)) return null;
  return {
    key: ROLE_REFUSAL_KEYS[code as RoleRefusalCodeType],
    facts: {
      roleKey: typeof data.roleKey === 'string' ? data.roleKey : null,
      roleNames: data.roleNames,
      ...(data.verb === 'remove' || data.verb === 'grant' ? { verb: data.verb } : {}),
      ...(typeof data.permission === 'string' ? { permission: data.permission } : {}),
      ...(data.during === 'install' ? { during: 'install' as const } : {}),
    },
  };
}
